import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatActionList } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { finalize } from 'rxjs';

import { IUserBasic, UserScope } from '@core/interfaces';
import { UserPhotoService } from '@core/services';

import { SnackBarService } from '@shared/service/snack-bar.service';
import { UserScopeService } from '@shared/service/user-scope.service';

interface UserScopeOption {
  value: UserScope;
  label: string;
}

const userScopeOptions: UserScopeOption[] = [
  { value: 'all', label: 'All Users' },
  { value: 'direct_reports', label: 'My Direct Reports' },
  { value: 'hierarchy', label: 'My Hierarchy' },
];

@Component({
  selector: 'app-user-profile',
  templateUrl: 'user-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    ::ng-deep .user-view-trigger .mat-mdc-menu-submenu-icon {
      order: -1;
      padding-right: var(--mat-menu-item-spacing, 12px);
      padding-left: 0;
    }

    ::ng-deep .profile-summary:hover,
    ::ng-deep .profile-summary:focus {
      background: transparent !important;
    }
  `,
  imports: [
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatActionList,
  ],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  user: IUserBasic | null = null;
  protected readonly profilePhotoUrl = signal<string | null>(null);

  private readonly destroyRef = inject(DestroyRef);
  private readonly msalService = inject(MsalService);
  private readonly userPhotoService = inject(UserPhotoService);
  private readonly snackBarService = inject(SnackBarService);
  private profilePhotoObjectUrl: string | null = null;
  private readonly router = inject(Router);
  protected readonly userScope = inject(UserScopeService);

  protected readonly visibleUserScopes = computed(() =>
    userScopeOptions.filter((option) => this.userScope.availableScopes().includes(option.value)),
  );
  protected readonly selectedUserScopeLabel = computed(() => {
    const selectedScope = this.userScope.selectedScope();
    return userScopeOptions.find((option) => option.value === selectedScope)?.label ?? '';
  });

  isSigningOut = false;

  ngOnInit() {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount?.idTokenClaims) return;

    this.user = {
      id: activeAccount.idTokenClaims.oid as string,
      email: activeAccount.idTokenClaims.preferred_username as string,
      first_name: activeAccount.idTokenClaims['given_name'] as string,
      last_name: activeAccount.idTokenClaims['family_name'] as string,
    };

    this.userPhotoService
      .getCurrentUserPhoto()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((photo) => this.setProfilePhoto(photo));
  }

  ngOnDestroy(): void {
    this.clearProfilePhoto();
  }

  protected handleProfilePhotoError(): void {
    this.clearProfilePhoto();
  }

  protected getProfileInitials(): string {
    return [this.user?.first_name, this.user?.last_name]
      .filter((name): name is string => Boolean(name))
      .map((name) => name.trim().charAt(0).toUpperCase())
      .join('');
  }

  private setProfilePhoto(photo: Blob | null): void {
    this.clearProfilePhoto();
    if (!photo) return;

    this.profilePhotoObjectUrl = URL.createObjectURL(photo);
    this.profilePhotoUrl.set(this.profilePhotoObjectUrl);
  }

  private clearProfilePhoto(): void {
    if (this.profilePhotoObjectUrl) {
      URL.revokeObjectURL(this.profilePhotoObjectUrl);
      this.profilePhotoObjectUrl = null;
    }
    this.profilePhotoUrl.set(null);
  }

  handleSignOut() {
    this.isSigningOut = true;

    this.msalService
      .logout()
      .pipe(finalize(() => (this.isSigningOut = false)))
      .subscribe({
        next: () => {
          this.router.navigate(['auth']);
        },
        error: () => {
          this.snackBarService.error('Something went wrong, please try again later');
        },
      });
  }
}
